import type { Editor } from "./editor";
import { Theme } from "DrawCore/src/style";

// # Overlays
//
// All the HTML stuff that sits "in front of" the canvas.
// Generally for UI stuff, e.g. editing parameters.
//
export class Overlays {
  // The DOM element that contains all our overlays.
  div: HTMLDivElement = document.createElement("div");

  // Status messages keyed by a unique identifier (e.g., "parasitics")
  private statusMessages: Map<string, HTMLDivElement> = new Map();

  // Container for status messages in the bottom-right corner
  private statusContainer: HTMLDivElement;

  constructor(
    public editor: Editor // Reference to the parent `Editor` instance.
  ) {
    const { style } = this.div;
    style.position = "absolute";
    style.top = "0";
    style.left = "0";
    style.width = "100%";
    style.height = "100%";
    style.pointerEvents = "none"; // Allow clicks to pass through to canvas
    style.zIndex = "2";

    // Create status message container in bottom-right
    this.statusContainer = document.createElement("div");
    this.statusContainer.style.position = "absolute";
    this.statusContainer.style.bottom = "10px";
    this.statusContainer.style.right = "10px";
    this.statusContainer.style.display = "flex";
    this.statusContainer.style.flexDirection = "column";
    this.statusContainer.style.alignItems = "flex-end";
    this.statusContainer.style.gap = "4px";
    this.statusContainer.style.pointerEvents = "none";
    this.div.appendChild(this.statusContainer);
  }

  // Add an element to the overlays.
  // Configures the overlay-ness of its `style`.
  add = (e: HTMLElement) => {
    e.style.position = "absolute";
    e.style.pointerEvents = "auto"; // Allow interaction with overlay elements

    // Position all overlays in top-left
    e.style.top = "10px";
    e.style.left = "10px";

    this.div.appendChild(e);
  };

  // Set a status message with a unique key
  setStatusMessage = (message: string, key: string) => {
    let element = this.statusMessages.get(key);

    if (!element) {
      // Create new status message element
      element = document.createElement("div");
      element.style.padding = "4px 8px";
      element.style.backgroundColor = "rgba(0, 100, 200, 0.85)";
      element.style.color = "white";
      element.style.fontSize = "12px";
      element.style.fontFamily = "monospace";
      element.style.borderRadius = "4px";
      element.style.whiteSpace = "nowrap";
      this.statusMessages.set(key, element);
      this.statusContainer.appendChild(element);
    }

    element.textContent = message;
  };

  // Clear a status message by key
  clearStatusMessage = (key: string) => {
    const element = this.statusMessages.get(key);
    if (element) {
      element.remove();
      this.statusMessages.delete(key);
    }
  };

  // Lock overlay element shown when the editor is locked (e.g., LLM query in flight).
  private lockOverlay: HTMLDivElement | null = null;

  // Show a lock indicator in the top-left corner of the viewport.
  showLockOverlay = () => {
    if (this.lockOverlay) return; // Already showing
    const el = document.createElement("div");
    el.style.position = "absolute";
    el.style.top = "10px";
    el.style.left = "10px";
    el.style.padding = "6px 10px";
    // Theme-aware colors: dark scheme keeps the original dark-grey-on-white
    // pill; light scheme uses a light background with dark text so it doesn't
    // look out of place against a white canvas.
    const isDark = Theme.isDark(Theme.get());
    el.style.backgroundColor = isDark
      ? "rgba(60, 60, 60, 0.85)"
      : "rgba(240, 240, 240, 0.9)";
    el.style.color = isDark ? "white" : "#1a1a1a";
    el.style.border = isDark
      ? "1px solid rgba(255, 255, 255, 0.15)"
      : "1px solid rgba(0, 0, 0, 0.15)";
    el.style.fontSize = "12px";
    el.style.fontFamily = "system-ui, sans-serif";
    el.style.borderRadius = "6px";
    el.style.pointerEvents = "none";
    el.style.userSelect = "none";
    el.style.zIndex = "10";
    el.textContent = "\u2728 Locked for AI editing"; // Emoji sparkle + text. An emoji is permitted in this context.
    this.lockOverlay = el;
    this.div.appendChild(el);
  };

  // Hide the lock indicator.
  hideLockOverlay = () => {
    if (!this.lockOverlay) return;
    this.lockOverlay.remove();
    this.lockOverlay = null;
  };
}
